/**
 * Firebase client SDK references for the connections feature. Paired with
 * the shapes + schemas + path helpers in `../documents/connections`.
 *
 * Mirrors `../admin/connectionsReferences` — same converter convention
 * (`validateOnRead: true`, `validateOnWrite: false`), same set of
 * doc/collection ref helpers, but built on `firebase/firestore` instead
 * of `firebase-admin/firestore`.
 *
 * `validateOnRead: true` rationale: 2026-05-04 production survey of every
 * connection-doc category hit 100% pass against these schemas.
 * `validateOnWrite: false` rationale: writes legitimately use
 * `serverTimestamp()` sentinels that Zod can't validate.
 */
import {
  collection,
  doc,
  getFirestore,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import {z} from 'zod';

import {
  ConnectionHistoryEntrySchema as BaseConnectionHistoryEntrySchema,
  ConnectionInconsistentDocumentSchema as BaseConnectionInconsistentDocumentSchema,
  ConnectionStateDocumentSchema,
  ConnectionSuggestionsDocumentSchema as BaseConnectionSuggestionsDocumentSchema,
  type ConnectionStateDocument,
  type PerUserConnectionDocument,
  type WantToConnectShardDocument,
  PerUserConnectionDocumentSchema,
  WantToConnectShardDocumentSchema,
  getConnectionHistoryCollectionPath,
  getConnectionHistoryDocPath,
  getConnectionInconsistentDocPath,
  getConnectionStateDocPath,
  getConnectionSuggestionsDocPath,
  getUserConnectionProtectedPairPath,
  getUserConnectionDocPath,
  getWantToConnectAdultsCollectionPath,
  getWantToConnectAdultsShardDocPath,
  type ConnectionProtectedPairSegment,
} from '../documents/connections';
import {ClientTimestampSchema, createConverter} from './sdk';

// ============================================================================
// SDK-specific schemas (client Timestamp transform)
// ============================================================================

/**
 * Client variant of `ConnectionHistoryEntrySchema` (`serverTimestamp` is the
 * client SDK `Timestamp` instance, post-transform).
 */
export const ConnectionHistoryEntrySchema =
  BaseConnectionHistoryEntrySchema.extend({
    serverTimestamp: ClientTimestampSchema,
  });
export type ConnectionHistoryEntry = z.infer<
  typeof ConnectionHistoryEntrySchema
>;

/** Client variant of `ConnectionSuggestionsDocumentSchema` with client Timestamp. */
export const ConnectionSuggestionsDocumentSchema =
  BaseConnectionSuggestionsDocumentSchema.extend({
    timestamp: ClientTimestampSchema,
  });
export type ConnectionSuggestionsDocument = z.infer<
  typeof ConnectionSuggestionsDocumentSchema
>;

/** Client variant of `ConnectionInconsistentDocumentSchema` with client Timestamp. */
export const ConnectionInconsistentDocumentSchema =
  BaseConnectionInconsistentDocumentSchema.extend({
    serverTimestamp: ClientTimestampSchema,
  });
export type ConnectionInconsistentDocument = z.infer<
  typeof ConnectionInconsistentDocumentSchema
>;

// ============================================================================
// Converters
// ============================================================================

const connectionStateConverter = createConverter<ConnectionStateDocument>({
  schema: ConnectionStateDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const perUserConnectionConverter = createConverter<PerUserConnectionDocument>({
  schema: PerUserConnectionDocumentSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const connectionHistoryConverter = createConverter<ConnectionHistoryEntry>({
  schema: ConnectionHistoryEntrySchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const connectionSuggestionsConverter =
  createConverter<ConnectionSuggestionsDocument>({
    schema: ConnectionSuggestionsDocumentSchema,
    options: {validateOnRead: true, validateOnWrite: false},
  });

const wantToConnectShardConverter = createConverter<WantToConnectShardDocument>(
  {
    schema: WantToConnectShardDocumentSchema,
    options: {validateOnRead: true, validateOnWrite: false},
  },
);

const connectionInconsistentConverter =
  createConverter<ConnectionInconsistentDocument>({
    schema: ConnectionInconsistentDocumentSchema,
    options: {validateOnRead: true, validateOnWrite: false},
  });

// ============================================================================
// Document refs
// ============================================================================

/**
 * Returns a typed client-SDK doc ref for the top-level pair-state doc.
 * `firestore` defaults to `getFirestore()`; pass an injected instance from
 * tests or harnesses that wire their own Firebase app.
 */
export function getConnectionStateDocRef({
  connectionString,
  firestore = getFirestore(),
}: {
  connectionString: string;
  firestore?: Firestore;
}): DocumentReference<ConnectionStateDocument> {
  return doc(
    firestore,
    getConnectionStateDocPath({connectionString}),
  ).withConverter(connectionStateConverter);
}

/**
 * Returns a typed client-SDK doc ref for one protected connection/request
 * pair path at `users/{uid}/docs/protected/<segment>/{otherUserId}`.
 */
export function getUserConnectionDocRef({
  userId,
  segment,
  otherUserId,
  firestore = getFirestore(),
}: {
  userId: string;
  segment: ConnectionProtectedPairSegment;
  otherUserId: string;
  firestore?: Firestore;
}): DocumentReference<PerUserConnectionDocument> {
  return doc(
    firestore,
    getUserConnectionDocPath({userId, segment, otherUserId}),
  ).withConverter(perUserConnectionConverter);
}

/** Returns a typed client-SDK collection ref for one protected pair segment. */
export function getUserConnectionCollectionRef({
  userId,
  segment,
  firestore = getFirestore(),
}: {
  userId: string;
  segment: ConnectionProtectedPairSegment;
  firestore?: Firestore;
}): CollectionReference<PerUserConnectionDocument> {
  return collection(
    firestore,
    getUserConnectionProtectedPairPath({userId, segment}),
  ).withConverter(perUserConnectionConverter);
}

/** Returns a typed client-SDK doc ref for one history entry under a pair. */
export function getConnectionHistoryDocRef({
  connectionString,
  timestampStr,
  firestore = getFirestore(),
}: {
  connectionString: string;
  /** ISO 8601 timestamp string used as the doc id. */
  timestampStr: string;
  firestore?: Firestore;
}): DocumentReference<ConnectionHistoryEntry> {
  return doc(
    firestore,
    getConnectionHistoryDocPath({connectionString, timestampStr}),
  ).withConverter(connectionHistoryConverter);
}

/** Returns a typed client-SDK collection ref for the history sub-collection of a pair. */
export function getConnectionHistoryCollectionRef({
  connectionString,
  firestore = getFirestore(),
}: {
  connectionString: string;
  firestore?: Firestore;
}): CollectionReference<ConnectionHistoryEntry> {
  return collection(
    firestore,
    getConnectionHistoryCollectionPath({connectionString}),
  ).withConverter(connectionHistoryConverter);
}

/** Returns a typed client-SDK doc ref for a per-user-per-course suggestions cache doc. */
export function getConnectionSuggestionsDocRef({
  courseId,
  userId,
  firestore = getFirestore(),
}: {
  courseId: string;
  userId: string;
  firestore?: Firestore;
}): DocumentReference<ConnectionSuggestionsDocument> {
  return doc(
    firestore,
    getConnectionSuggestionsDocPath({courseId, userId}),
  ).withConverter(connectionSuggestionsConverter);
}

/** Returns a typed client-SDK doc ref for a single wantToConnect shard. */
export function getWantToConnectAdultsShardDocRef({
  nShards,
  shardIndex,
  firestore = getFirestore(),
}: {
  nShards: number;
  shardIndex: number;
  firestore?: Firestore;
}): DocumentReference<WantToConnectShardDocument> {
  return doc(
    firestore,
    getWantToConnectAdultsShardDocPath({nShards, shardIndex}),
  ).withConverter(wantToConnectShardConverter);
}

/** Returns a typed client-SDK collection ref over all wantToConnect shards. */
export function getWantToConnectAdultsCollectionRef({
  firestore = getFirestore(),
}: {
  firestore?: Firestore;
} = {}): CollectionReference<WantToConnectShardDocument> {
  return collection(
    firestore,
    getWantToConnectAdultsCollectionPath(),
  ).withConverter(wantToConnectShardConverter);
}

/** Returns a typed client-SDK doc ref for a `connectionsInconsistent/{pairId}` sentinel. */
export function getConnectionInconsistentDocRef({
  connectionString,
  firestore = getFirestore(),
}: {
  connectionString: string;
  firestore?: Firestore;
}): DocumentReference<ConnectionInconsistentDocument> {
  return doc(
    firestore,
    getConnectionInconsistentDocPath({connectionString}),
  ).withConverter(connectionInconsistentConverter);
}
